import { useMemo, useState } from "react";
import styles from "../styles/runner.module.css";
import type { DragSortConfig } from "../types";

interface Props {
  prompt: string;
  config: DragSortConfig;
  passingScore: number;
  onScored: (result: {
    score: number;
    passed: boolean;
    raw: { mapping: Record<string, string> };
  }) => void;
}

/**
 * Tap-to-place fallback (no HTML5 drag-and-drop yet). The user picks an item,
 * then taps a bucket. This works smoothly in both desktop and mobile WebViews
 * and skips the DnD-API quirks on iOS. Real DnD with haptic-on-drop is
 * tracked for Phase 2.
 */
export function DragSort({ prompt, config, passingScore, onScored }: Props) {
  const itemIds = useMemo(() => config.items.map((i) => i.id), [config.items]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);

  function pickItem(id: string) {
    if (submitted) return;
    setSelectedItem((curr) => (curr === id ? null : id));
  }

  function placeIn(bucketId: string) {
    if (submitted || !selectedItem) return;
    setMapping((curr) => ({ ...curr, [selectedItem]: bucketId }));
    setSelectedItem(null);
  }

  function submit() {
    if (submitted) return;
    if (Object.keys(mapping).length !== itemIds.length) return;
    let correct = 0;
    for (const id of itemIds) {
      if (mapping[id] === config.correct_mapping[id]) correct += 1;
    }
    const s = itemIds.length === 0 ? 0 : correct / itemIds.length;
    const ok = s >= passingScore;
    setScore(s);
    setPassed(ok);
    setSubmitted(true);
    onScored({
      score: s,
      passed: ok,
      raw: { mapping },
    });
  }

  // Unplaced items live in a "tray" on top.
  const placedIds = new Set(Object.keys(mapping));
  const trayItems = config.items.filter((i) => !placedIds.has(i.id));

  return (
    <div className={styles.col}>
      <h2 className={styles.prompt}>{prompt}</h2>

      <div>
        <div className={styles.bucketTitle}>Por colocar</div>
        <div className={styles.row}>
          {trayItems.map((i) => (
            <button
              key={i.id}
              type="button"
              className={[
                styles.dragItem,
                selectedItem === i.id ? styles.dragItemActive : "",
              ].join(" ")}
              onClick={() => pickItem(i.id)}
              disabled={submitted}
            >
              {i.label}
            </button>
          ))}
          {trayItems.length === 0 && (
            <span style={{ color: "#9ca3af", fontSize: 14 }}>
              Todos colocados — pulsa Validar.
            </span>
          )}
        </div>
      </div>

      <div className={styles.grid2}>
        {config.buckets.map((b) => {
          const itemsHere = config.items.filter((i) => mapping[i.id] === b.id);
          return (
            <button
              key={b.id}
              type="button"
              className={styles.bucket}
              onClick={() => placeIn(b.id)}
              disabled={submitted || !selectedItem}
              style={{ textAlign: "left", cursor: selectedItem ? "pointer" : "default" }}
            >
              <span className={styles.bucketTitle}>{b.label}</span>
              {itemsHere.map((i) => {
                const isCorrect = submitted && config.correct_mapping[i.id] === b.id;
                const isWrong = submitted && config.correct_mapping[i.id] !== b.id;
                const cls = [
                  styles.dragItem,
                  isCorrect ? styles.optionCorrect : "",
                  isWrong ? styles.optionWrong : "",
                ].join(" ");
                return (
                  <span key={i.id} className={cls}>
                    {i.label}
                  </span>
                );
              })}
            </button>
          );
        })}
      </div>

      {!submitted ? (
        <button
          type="button"
          className={styles.primaryButton}
          disabled={Object.keys(mapping).length !== itemIds.length}
          onClick={submit}
        >
          Validar colocación
        </button>
      ) : (
        <div className={passed ? styles.summary : `${styles.summary} ${styles.summaryFail}`}>
          <span className={styles.scoreBadge}>{Math.round(score * 100)}%</span>
          <strong>{passed ? "¡Excelente!" : "Casi"}</strong>
          <span>Mínimo requerido: {Math.round(passingScore * 100)}%</span>
        </div>
      )}
    </div>
  );
}
